import React, { useState, useEffect, useRef, useCallback } from 'react';
import db from '../../utils/database';


const TableViewer = ({ tableName, password }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('');
  const [orderDirection, setOrderDirection] = useState('asc');
  
  // Filtering state
  const [filters, setFilters] = useState({});
  const [filterValue, setFilterValue] = useState('');
  const [filterColumn, setFilterColumn] = useState('');
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Editing state
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, column }
  const [editValue, setEditValue] = useState('');
  const [savingRow, setSavingRow] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);

  // No column width customization functionality

  // Load data when component mounts or when parameters change
  useEffect(() => {
    if (tableName) {
      loadTableData();
    } else {
      // If no table is selected, set loading to false
      setLoading(false);
    }
  }, [tableName, page, rowsPerPage, orderBy, orderDirection, filters]);
  
  // Load table data with pagination, sorting, and filtering
  const loadTableData = async () => {
    setLoading(true);
    setError(null);

    try {
      // First, let's try to get the column names if we don't have them yet
      if (columns.length === 0) {
        try {
          // Get a single row to extract column names
          const sampleResult = await db.query(`SELECT * FROM ${tableName} LIMIT 1`);
          if (sampleResult.rows && sampleResult.rows.length > 0) {
            setColumns(Object.keys(sampleResult.rows[0]));
          }
        } catch (colErr) {
          console.warn("Could not get column names from sample row:", colErr);
          // Continue with query - we'll try to get columns from the main query result
        }
      }

      // Build the SQL query with pagination, sorting, and filtering
      let query = `SELECT * FROM ${tableName}`;

      // Add WHERE clauses for filters
      if (Object.keys(filters).length > 0) {
        const whereClauses = Object.entries(filters)
          .filter(([_, value]) => value)
          .map(([column, value]) => `"${column}" LIKE '%${value}%'`);

        if (whereClauses.length > 0) {
          query += ' WHERE ' + whereClauses.join(' AND ');
        }
      }
      
      // Add search term across all columns if provided
      if (searchTerm && columns.length > 0) {
        // Ensure column names are properly quoted to prevent SQL errors
        const whereSearch = columns
          .map(column => `"${column}" LIKE '%${searchTerm}%'`)
          .join(' OR ');

        if (whereSearch) {
          query += Object.keys(filters).length > 0 ? ' AND (' + whereSearch + ')' : ' WHERE ' + whereSearch;
        }
      }
      
      // Add order by clause if sorting is specified
      if (orderBy) {
        query += ` ORDER BY "${orderBy}" ${orderDirection}`;
      }
      
      // Add limit and offset for pagination
      query += ` LIMIT ${rowsPerPage} OFFSET ${page * rowsPerPage}`;
      
      // Execute the query
      const result = await db.query(query);

      // Get total row count for pagination
      const countResult = await db.query(`SELECT COUNT(*) as total FROM ${tableName}`);

      // Update state with results
      setData(result.rows);

      // Update columns if we have rows and don't have columns yet
      if (result.rows.length > 0 && columns.length === 0) {
        setColumns(Object.keys(result.rows[0]));
      }

      setTotalRows(countResult.rows[0].total);
    } catch (err) {
      setError(`Failed to load table data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle sort toggle
  const handleSort = (column) => {
    const isAsc = orderBy === column && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle filter add
  const handleAddFilter = () => {
    if (filterColumn && filterValue) {
      setFilters({
        ...filters,
        [filterColumn]: filterValue,
      });
      setFilterColumn('');
      setFilterValue('');
      setPage(0); // Reset to first page when filter changes
    }
  };
  
  // Handle filter remove
  const handleRemoveFilter = (column) => {
    const newFilters = { ...filters };
    delete newFilters[column];
    setFilters(newFilters);
    setPage(0); // Reset to first page when filter changes
  };
  
  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when search changes

    // If we don't have columns yet and there's a search term,
    // trigger a new load to first fetch columns
    if (columns.length === 0 && event.target.value) {
      loadTableData();
    }
  };
  
  // Start editing a cell
  const handleCellClick = (rowIndex, column, value) => {
    setEditingCell({ rowIndex, column });
    setEditValue(value);
  };

  // Handle edit value change
  const handleEditChange = (e) => {
    setEditValue(e.target.value);
  };

  // Handle saving edited cell
  const handleSaveEdit = async () => {
    if (!editingCell) return;

    const { rowIndex, column } = editingCell;
    const rowData = data[rowIndex];

    try {
      setSavingRow(rowIndex);

      // Create an SQL update query
      const rowIdentifier = getRowIdentifier(rowData);

      if (!rowIdentifier) {
        throw new Error("Could not identify row for update");
      }

      const query = `UPDATE "${tableName}" SET "${column}" = ? WHERE ${rowIdentifier.condition}`;

      await db.query(query, [editValue, ...rowIdentifier.values]);

      // Update local data
      const updatedData = [...data];
      updatedData[rowIndex] = { ...rowData, [column]: editValue };
      setData(updatedData);

      // Reset editing state
      setEditingCell(null);
      setEditValue('');
    } catch (err) {
      setError(`Error updating cell: ${err.message}`);
    } finally {
      setSavingRow(null);
    }
  };

  // Handle cancelling edit
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Get row identifier for update/delete operations
  const getRowIdentifier = (row) => {
    // Try to use primary key if available
    // For this simple version, we'll use all columns to identify the row
    // In a more sophisticated version, you'd want to use the primary key

    const conditions = [];
    const values = [];

    Object.entries(row).forEach(([col, val]) => {
      if (val !== null) {
        conditions.push(`"${col}" = ?`);
        values.push(val);
      } else {
        conditions.push(`"${col}" IS NULL`);
      }
    });

    if (conditions.length === 0) return null;

    return {
      condition: conditions.join(' AND '),
      values
    };
  };

  // Handle deleting a row
  const handleDeleteRow = async (rowIndex) => {
    const rowData = data[rowIndex];

    try {
      setDeletingRow(rowIndex);

      const rowIdentifier = getRowIdentifier(rowData);

      if (!rowIdentifier) {
        throw new Error("Could not identify row for deletion");
      }

      const query = `DELETE FROM "${tableName}" WHERE ${rowIdentifier.condition}`;

      await db.query(query, rowIdentifier.values);

      // Update local data
      const updatedData = [...data];
      updatedData.splice(rowIndex, 1);
      setData(updatedData);
      setTotalRows(totalRows - 1);

    } catch (err) {
      setError(`Error deleting row: ${err.message}`);
    } finally {
      setDeletingRow(null);
    }
  };

  // Column resizing removed

  // Keyboard handling for the edit input
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Calculate pagination information
  const pageCount = Math.ceil(totalRows / rowsPerPage);
  const startRow = page * rowsPerPage + 1;
  const endRow = Math.min((page + 1) * rowsPerPage, totalRows);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }
  
  if (!tableName) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a table to view data.</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">

      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Table: {tableName}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Browse, search, and filter table data.
        </p>
      </div>
      
      {/* Search and filter controls */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-4">
          {/* Global search */}
          <div className="flex-grow">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search across all columns..."
              className="p-2 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          
          {/* Column filter */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label htmlFor="filterColumn" className="block text-sm font-medium text-gray-700 mb-1">
                Filter Column
              </label>
              <select
                id="filterColumn"
                value={filterColumn}
                onChange={(e) => setFilterColumn(e.target.value)}
                className="p-2 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Select column</option>
                {columns.map((column) => (
                  <option key={column} value={column}>
                    {column}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="filterValue" className="block text-sm font-medium text-gray-700 mb-1">
                Filter Value
              </label>
              <input
                type="text"
                id="filterValue"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                placeholder="Filter value..."
                className="p-2 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            
            <button
              onClick={handleAddFilter}
              disabled={!filterColumn || !filterValue}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              Add Filter
            </button>
          </div>
        </div>
        
        {/* Active filters */}
        {Object.keys(filters).length > 0 && (
          <div className="mt-4">
            <div className="text-sm font-medium text-gray-700 mb-2">Active Filters:</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(filters).map(([column, value]) => (
                <span
                  key={column}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
                >
                  {column}: {value}
                  <button
                    type="button"
                    onClick={() => handleRemoveFilter(column)}
                    className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-indigo-400 hover:text-indigo-500 focus:outline-none focus:text-indigo-500"
                  >
                    <span className="sr-only">Remove filter</span>
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Data table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {/* Delete column */}
              <th
                scope="col"
                className="w-12 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Delete
              </th>

              {columns.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="relative group px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center relative">
                    {column}
                    {orderBy === column ? (
                      <span className="ml-1">
                        {orderDirection === 'asc' ? (
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>
                    ) : null}

                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {/* Delete button cell */}
                <td className="w-12 px-3 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleDeleteRow(rowIndex)}
                    disabled={deletingRow === rowIndex}
                    className="text-red-500 hover:text-red-700 disabled:opacity-50"
                    title="Delete row"
                  >
                    {deletingRow === rowIndex ? (
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </td>

                {columns.map((column) => {
                  const cellValue = typeof row[column] === 'object' ? JSON.stringify(row[column]) : String(row[column] || '');
                  const isEditing = editingCell && editingCell.rowIndex === rowIndex && editingCell.column === column;
                  const isSaving = savingRow === rowIndex;

                  return (
                    <td
                      key={`${rowIndex}-${column}`}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                      onClick={() => !isEditing && !isSaving && handleCellClick(rowIndex, column, cellValue)}
                    >
                      {isEditing ? (
                        <div className="flex items-center">
                          <input
                            type="text"
                            value={editValue}
                            onChange={handleEditChange}
                            onKeyDown={handleEditKeyDown}
                            autoFocus
                            className="p-1 border rounded-md border-indigo-500 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 w-full"
                          />
                          <div className="flex ml-2">
                            <button
                              onClick={handleSaveEdit}
                              disabled={isSaving}
                              className="text-green-500 hover:text-green-700 disabled:opacity-50 mr-1"
                              title="Save"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                              className="text-red-500 hover:text-red-700 disabled:opacity-50"
                              title="Cancel"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={`cursor-pointer border border-transparent hover:border-gray-300 p-1 rounded ${isSaving ? 'opacity-50' : ''}`}>
                          {cellValue}
                          {isSaving && (
                            <span className="ml-2 inline-block">
                              <svg className="animate-spin h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                              </svg>
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination controls */}
      <div className="px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex items-center">
            <label htmlFor="rowsPerPage" className="mr-2 text-sm text-gray-700">
              Rows per page:
            </label>
            <select
              id="rowsPerPage"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              className="p-1 border rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {[10, 25, 50, 100].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mt-2 sm:mt-0">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{startRow}</span> to{' '}
              <span className="font-medium">{endRow}</span> of{' '}
              <span className="font-medium">{totalRows}</span> results
            </p>
          </div>
          
          <div className="mt-2 sm:mt-0">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(0)}
                disabled={page === 0}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">First</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              
              {/* Page buttons */}
              {Array.from({ length: Math.min(5, pageCount) }).map((_, i) => {
                // Calculate page number to show
                let pageToShow;
                if (pageCount <= 5) {
                  pageToShow = i;
                } else if (page < 3) {
                  pageToShow = i;
                } else if (page > pageCount - 4) {
                  pageToShow = pageCount - 5 + i;
                } else {
                  pageToShow = page - 2 + i;
                }
                
                return (
                  <button
                    key={i}
                    onClick={() => handlePageChange(pageToShow)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === pageToShow
                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageToShow + 1}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= pageCount - 1}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <button
                onClick={() => handlePageChange(pageCount - 1)}
                disabled={page >= pageCount - 1}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Last</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 15.707a1 1 0 001.414 0l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 1.414L8.586 10 4.293 14.293a1 1 0 000 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 15.707a1 1 0 001.414 0l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 1.414L8.586 10 4.293 14.293a1 1 0 000 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableViewer;